/**
 * Create a playEngine base — the replacement for a JSON file, a src/games
 * folder and a deploy. Open to a superAdmin and to users it granted
 * manageRuntimeBases.
 *
 * Three kinds: a versionedPlayEngine base (versions in Mongo, the media library),
 * a snapshotEditor base bound to one agentContainer flow, and a pe3d
 * (PlayEngine3D) base — a shelf for variants of any published 3D game; its game
 * only says where it is listed, since each variant names its own. The flow
 * list comes from the container; a flow whose pool this server cannot see is
 * shown but cannot be picked, because the server would refuse it anyway.
 */

import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import { MUTED } from "./settingsPalette";
import { findAllGames, insertBase, listBaseFlows } from "../../modules/adminApi";

const useStyles = makeStyles(() => ({
  field: { width: "100%", marginBottom: "1rem" },
  hint: { fontSize: "0.75rem", color: MUTED, marginBottom: "0.75rem" },
  kindHint: { fontSize: "0.7rem", color: MUTED, display: "block", lineHeight: 1.4 },
}));

const KIND = { VERSIONED: "versionedPlayEngine", EDITOR: "snapshotEditor", PE3D: "pe3d" };

const ERROR_TEXT = {
  GAME_WAS_NOT_FOUND: "That game no longer exists.",
  UNKNOWN_FLOW: "The agent container does not run a flow by that name.",
  SNAPSHOT_FLOW_IS_NOT_MOUNTED: "This server cannot reach that flow's snapshot pool.",
  AGENT_CONTAINER_UNAVAILABLE: "The agent container is unreachable; try again later.",
  FLOW_ALREADY_HAS_A_BASE: "Another base is already bound to that flow.",
};

export default function DialogBaseCreate(props) {
  const classes = useStyles();
  // initialKind: the PE3D games panel opens it on pe3d.
  const { isOpen, onClose, onCreated, createSnackbar, initialKind = KIND.VERSIONED } = props;

  const [games, setGames] = useState([]);
  const [flows, setFlows] = useState([]);
  const [flowsAvailable, setFlowsAvailable] = useState(true);
  const [gameID, setGameID] = useState("");
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState(KIND.VERSIONED);
  const [flow, setFlow] = useState("");
  const [taskDelivery, setTaskDelivery] = useState(false);
  const [exportAsVersion, setExportAsVersion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle("");
    setKind(initialKind);
    setFlow("");
    setTaskDelivery(false);
    setExportAsVersion(false);
    Promise.all([findAllGames(), listBaseFlows()]).then(([gameResult, flowResult]) => {
      const gameList = gameResult.ok ? gameResult.data || [] : [];
      setGames(gameList);
      setGameID((current) => current || (gameList[0] && gameList[0]._id) || "");
      setFlowsAvailable(Boolean(flowResult.ok && flowResult.data && flowResult.data.available));
      setFlows(flowResult.ok && flowResult.data ? flowResult.data.flows || [] : []);
    });
  }, [isOpen]);

  const canSubmit =
    !isSaving && gameID && title.trim().length > 0 && (kind !== KIND.EDITOR || flow);

  const submit = async () => {
    setIsSaving(true);
    const result = await insertBase({
      gameID,
      title: title.trim(),
      kind,
      ...(kind === KIND.EDITOR ? { flow, taskDelivery, exportAsVersion } : {}),
    });
    setIsSaving(false);

    if (!result.ok) {
      const code = typeof result.data === "string" ? result.data : result.data && (result.data.error || JSON.stringify(result.data));
      createSnackbar(`Could not create the base: ${ERROR_TEXT[code] || code || result.status}`, "error", false);
      return;
    }
    // A creator who is not a superAdmin has it granted to its own company by the
    // server (base/insertOne), and cannot grant it anywhere else.
    createSnackbar(
      result.data && result.data.grantedToCompanyID
        ? `${title.trim()} created and granted to your company.`
        : `${title.trim()} created. Grant it to a company next.`,
      "success",
      false
    );
    onCreated(result.data);
  };

  return (
    <Dialog open={Boolean(isOpen)} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New base</DialogTitle>
      <DialogContent>
        <Typography className={classes.hint}>
          Created in the database right away — no file, no deploy. Legacy game bases still come from
          <code> database/base/</code>.
        </Typography>

        <TextField
          select
          className={classes.field}
          size="small"
          label="Game"
          value={gameID}
          onChange={(event) => setGameID(event.target.value)}
        >
          {games.map((game) => (
            <MenuItem key={game._id} value={game._id}>
              {game.title}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          className={classes.field}
          size="small"
          label="Base title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          inputProps={{ maxLength: 120 }}
        />

        <RadioGroup value={kind} onChange={(event) => setKind(event.target.value)}>
          <FormControlLabel
            value={KIND.VERSIONED}
            control={<Radio size="small" />}
            label={
              <span>
                Versioned playEngine
                <span className={classes.kindHint}>Versions in the dashboard, the media library, customer exports.</span>
              </span>
            }
          />
          <FormControlLabel
            value={KIND.EDITOR}
            control={<Radio size="small" />}
            label={
              <span>
                Snapshot editor
                <span className={classes.kindHint}>Edits the snapshot files of one agent pipeline.</span>
              </span>
            }
          />
          <FormControlLabel
            value={KIND.PE3D}
            control={<Radio size="small" />}
            label={
              <span>
                PlayEngine3D
                <span className={classes.kindHint}>Variants of published 3D games; one base can hold several, under any game.</span>
              </span>
            }
          />
        </RadioGroup>

        {kind === KIND.EDITOR && (
          <div style={{ marginTop: "0.75rem" }}>
            {!flowsAvailable && (
              <Typography className={classes.hint}>
                The agent container is unreachable, so no flow can be chosen right now.
              </Typography>
            )}
            <TextField
              select
              className={classes.field}
              size="small"
              label="Flow"
              value={flow}
              onChange={(event) => setFlow(event.target.value)}
              disabled={!flowsAvailable}
            >
              {flows.map((item) => (
                <MenuItem key={item.name} value={item.name} disabled={!item.mounted}>
                  {item.name}
                  {!item.mounted ? " (pool not mounted here)" : ""}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Checkbox size="small" checked={taskDelivery} onChange={(event) => setTaskDelivery(event.target.checked)} />}
              label="Task delivery (AppLovin preview, Drive, Asana)"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={exportAsVersion} onChange={(event) => setExportAsVersion(event.target.checked)} />}
              label="Export snapshots as versions into other bases"
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={submit} disabled={!canSubmit}>
          Create base
        </Button>
      </DialogActions>
    </Dialog>
  );
}
